import _jsx from "@babel/runtime/helpers/jsx";
var _GradientOverlay, _GradientOverlay2;
import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';
import { deprecate } from 'react-is-deprecated';
import { GradientOverlay, ArtDirectionImage, PositionedOverlay, PositionedOverlayAnchor, Link } from 'reaxl';
const HeroImg = /*#__PURE__*/React.memo(_ref => {
  let {
    src,
    lgSrc,
    mdSrc,
    smSrc,
    xsSrc,
    className,
    alt
  } = _ref;
  return /*#__PURE__*/_jsx(ArtDirectionImage, {
    src: src,
    smSrc: smSrc,
    xsSrc: xsSrc,
    mdSrc: mdSrc,
    lgSrc: lgSrc,
    className: className,
    alt: alt
  });
});

/**
 *  Hero Component is used to standardize the rendering of Hero Images as well as the placement
 *  of child elements within it.
 */
function Hero(_ref2) {
  let {
    alt,
    block = false,
    bottomGradient = false,
    children,
    className,
    color,
    contentPosition = 'center',
    gradient = true,
    isSponsoredByTextWhite = true,
    lgSrc,
    link,
    logo,
    mdSrc,
    size,
    smSrc,
    src = '//content.autotrader.com/content/dam/dealersite/images/blank_dot_002.gif',
    // this is to prevent the white border from showing when the src is not defined yet,
    uiContext = 'landing',
    xsSrc,
    ...rest
  } = _ref2;
  if (size) {
    uiContext = size === 'lg' ? 'billboard' : 'landing';
  }
  const heroClassNames = clsx({
    [uiContext]: true,
    hero: true,
    'container-fluid': !block
  }, className);
  const containerClassName = clsx({
    [uiContext]: true,
    container: true,
    'hero-container': true
  });
  const overlayClassName = clsx({
    [uiContext]: true,
    'container-fluid': !block,
    'hero-positioned-overlay padding-sm-0': true,
    'padding-3': uiContext !== 'superhero'
  });
  const baseImgClassName = clsx({
    [uiContext]: true,
    'hero-base-img': true
  });
  const heroWrapperStyles = {
    backgroundImage: src && src.length > 0 && "url('" + src + "')"
  };
  //  TODO: color should be removed, it is not used on Home Page and seems to
  //  only be added b/c of how images were made for loan page
  const containerStyle = {
    background: color
  };
  const renderGradientOverlay = block && gradient && (_GradientOverlay || (_GradientOverlay = /*#__PURE__*/_jsx(GradientOverlay, {
    direction: "short",
    className: "gradient-overlay"
  })));
  const renderGradientOverlayBottomToTop = block && bottomGradient && (_GradientOverlay2 || (_GradientOverlay2 = /*#__PURE__*/_jsx(GradientOverlay, {
    direction: "bottom-to-top short",
    className: "gradient-overlay"
  })));
  const buildLogo = () => {
    const {
      src: logoSrc,
      xsSrc: logoXsSrc = logoSrc,
      smSrc: logoSmSrc = logoSrc,
      mdSrc: logoMdSrc = logoSrc,
      lgSrc: logoLgSrc = logoSrc,
      width: logoWidth,
      height: logoHeight,
      alt: logoAlt,
      ...linkProps
    } = logo;
    const sponsoredByWordClassNames = clsx({
      'text-inverse': isSponsoredByTextWhite,
      'text-gray-base': !isSponsoredByTextWhite
    }, 'margin-right-2 text-size-200');
    const logoContent = /*#__PURE__*/_jsx(ArtDirectionImage, {
      src: logoSrc,
      xsSrc: logoXsSrc,
      smSrc: logoSmSrc,
      mdSrc: logoMdSrc,
      lgSrc: logoLgSrc,
      width: logoWidth,
      height: logoHeight,
      alt: logoAlt,
      lazyload: "false",
      responsive: false
    });
    return !!logo && /*#__PURE__*/_jsx("div", {
      className: "margin-top-1 margin-top-md-0"
    }, void 0, /*#__PURE__*/_jsx("span", {
      className: sponsoredByWordClassNames
    }, void 0, "Sponsored By"), linkProps.href ? /*#__PURE__*/React.createElement(Link, Object.assign({
      label: logoContent
    }, linkProps)) : logoContent);
  };
  return /*#__PURE__*/React.createElement("div", Object.assign({
    "data-cmp": "hero",
    className: heroClassNames
  }, rest), uiContext !== 'full' && /*#__PURE__*/_jsx("span", {
    className: "visible-lg"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "heroImgWrapper",
    style: heroWrapperStyles
  })), renderGradientOverlay, renderGradientOverlayBottomToTop, /*#__PURE__*/_jsx("div", {
    className: containerClassName,
    style: containerStyle
  }, void 0, /*#__PURE__*/_jsx(HeroImg, {
    src: src,
    smSrc: smSrc,
    xsSrc: xsSrc,
    mdSrc: mdSrc,
    lgSrc: lgSrc,
    className: baseImgClassName,
    alt: alt || 'Hero Image'
  }), /*#__PURE__*/_jsx(PositionedOverlay, {
    className: overlayClassName
  }, void 0, /*#__PURE__*/_jsx(PositionedOverlayAnchor, {
    className: "hero-positioned-overlay-anchor",
    position: contentPosition,
    block: block
  }, void 0, children), logo && /*#__PURE__*/_jsx(PositionedOverlayAnchor, {
    className: "padding-bottom-2",
    position: "bottomRight"
  }, void 0, buildLogo()), link && /*#__PURE__*/React.createElement(Link, Object.assign({
    className: "hero-img-link"
  }, link)))));
}
export default Hero;